<script setup lang="ts">
const { requiredRule, emailRule, isPositiveIntegerRule, minValueRule, maxValueRule } = useFormRulesService()
const loader = ref(false)
const notyf = useNotyfService()
const formRef = ref()
const phoneInputRef = ref()
const isValid = ref(false)
const isFormPhoneValid = ref(true)
const phoneInputTouched = ref(false)
const $t = useI18n().t
const localePath = useLocalePath()

const props = defineProps<{
	minVal: number
	maxVal: number
	label?: EFormLabel
	car: IProductModel
	timeframe: EContactFormRentalTimeframe
	discountPercentage?: number
}>()

const { isMobile_xl } = useWindowWidth()
const formValues = ref({
	radioValue: EContactFormClientType.COMPANY,
	name: '',
	email: '',
	companyName: '',
	phoneNumber: undefined,
	cars: 1,
	sliderValue: props.minVal,
	consentToProcessData: false,
	marketingConsent: false,
})

const hasInteracted = ref(false)
provide('hasInteracted', hasInteracted)

const minValue = 1
const maxValue = 50

const handlePhoneBlur = () => {
	phoneInputTouched.value = true
}

const getGTMParameters = () => ({
	form_name: 'Sprawdź naszą specjalną ofertę',
	form_rental_period: formValues.value.sliderValue,
	car_id: props.car?.id ?? null,
	car_name: getCarNameLabel(props.car),
})

const sendFormStarted = () => {
	const gtmService = useGTMService()
	if (!gtmService) return
	gtmService.formStarted(getGTMParameters())
}

const sendFormSubmitted = () => {
	const gtmService = useGTMService()
	if (!gtmService) return
	gtmService.formSubmitted({ ...getGTMParameters(), form_email: formValues.value.email, form_email_hash: hashEmail(formValues.value.email) })
}

onMounted(() => {
	sendFormStarted()
})

const carPrice = (car: IProductModel) => {
	const price = car.segment.promotionalPrice ? car.segment.promotionalPrice : car.segment.regularPrice

	if (!price?.amountGross) return 0

	const discount = props.discountPercentage ? props.discountPercentage / 100 : 0

	return (price?.amountGross - price?.amountGross * discount).toFixed(2) + ' ' + price?.currency + ' ' + '/ za dzień'
}

const submit = async () => {
	if (!isFormValid.value) return
	loader.value = true

	try {
		const obj: IContactFormRequestModel = {
			rentalTimeframe: props.timeframe,
			clientType: formValues.value.radioValue,
			clientName: formValues.value.name,
			clientEmail: formValues.value.email,
			clientPhoneNumber: formValues.value.phoneNumber ?? '',
			clientCompanyName: formValues.value.companyName,
			rentalDays: props.label === EFormLabel.DAYS ? formValues.value.sliderValue : formValues.value.sliderValue * 30,
			rentalCarsCount: formValues.value.cars,
			carModelName: props.car.fullName,
			carSegmentName: props.car.segment.name,
			promoCarPrice: carPrice(props.car),
			marketingConsent: formValues.value.marketingConsent,
		}

		await ApiService.sendContactForm(obj)
		notyf?.success($t('success.inquiry_sent_to_carfree'))
		sendFormSubmitted()

		formRef.value.reset()
		formRef.value.resetValidation()
		formValues.value.sliderValue = props.minVal
		formValues.value.cars = 1
		formValues.value.consentToProcessData = false
		formValues.value.marketingConsent = false
		formValues.value.phoneNumber = undefined
		phoneInputTouched.value = false
		isFormPhoneValid.value = true
		formValues.value.radioValue = EContactFormClientType.COMPANY
		formValues.value.companyName = ''
		formValues.value.name = ''
		formValues.value.email = ''
		hasInteracted.value = false
	} catch (error) {
		logger.error(error)
	} finally {
		loader.value = false
	}
}

const isFormValid = computed(() => {
	return isValid.value && isFormPhoneValid.value
})
</script>

<template>
	<v-form ref="formRef" v-model="isValid" class="form">
		<v-radio-group v-model="formValues.radioValue" class="form__company" inline :hide-details="true">
			<GlobalRadio
				color="var(--primary-color)"
				:label="$t('modules.long_term_page_module.car_inquiry_modal.form.private_individual')"
				:value="EContactFormClientType.INDIVIDUAL"></GlobalRadio>
			<GlobalRadio
				color="var(--primary-color)"
				:label="$t('modules.long_term_page_module.car_inquiry_modal.form.company')"
				:value="EContactFormClientType.COMPANY"></GlobalRadio>
		</v-radio-group>
		<div class="form__inputs">
			<InputPrimary
				v-model="formValues.name"
				:label="$t('modules.long_term_page_module.car_inquiry_modal.form.first_and_last_name')"
				required
				:rules="[requiredRule]"></InputPrimary>
			<InputPrimary
				v-model="formValues.email"
				:label="$t('modules.long_term_page_module.car_inquiry_modal.form.email')"
				required
				:rules="[requiredRule, emailRule]"></InputPrimary>
			<div
				class="form__phone"
				:class="{
					'form__phone--full': formValues.radioValue !== 'company',
					'form__phone--error': !isFormPhoneValid && phoneInputTouched,
				}">
				<LazyGlobalPhoneInput
					ref="phoneInputRef"
					v-model="formValues.phoneNumber"
					:label="$t('components.modal.login.phone')"
					required
					:rules="[requiredRule]"
					:validate-on="['blur', 'input']"
					@click="handlePhoneBlur"
					@update="(v) => (isFormPhoneValid = v)" />
			</div>
			<InputPrimary
				v-if="formValues.radioValue === 'company'"
				v-model="formValues.companyName"
				:label="$t('modules.long_term_page_module.car_inquiry_modal.form.company_name')"
				required
				:rules="[requiredRule]"></InputPrimary>
		</div>
		<div class="form__slider-container">
			<label>
				{{ $t('modules.long_term_page_module.car_inquiry_modal.form.rental_period') }}
				{{
					label && label === EFormLabel.MONTHS
						? `(${$t('common.months')})`
						: label && label === EFormLabel.DAYS
							? `(${$t('common.days')})`
							: ''
				}}
			</label>
			<v-slider
				v-model="formValues.sliderValue"
				name="Liczba miesięcy"
				thumb-color="#fff"
				track-fill-color="var(--primary-color)"
				track-color="var(--stroke-on-background)"
				:max="props.maxVal"
				:min="props.minVal"
				:step="1"
				:hide-details="true"
				:track-size="isMobile_xl ? 3 : 4"
				thumb-label></v-slider>
			<div class="form__slider-months">
				<span>{{ props.minVal }}</span>
				<span>{{ props.maxVal }}</span>
			</div>
		</div>
		<div v-if="formValues.radioValue === 'company'" class="form__number-of-cars">
			<InputPrimary
				v-model.number="formValues.cars"
				type="number"
				:label="$t('modules.long_term_page_module.car_inquiry_modal.form.number_of_cars_to_rent')"
				required
				:rules="[requiredRule, isPositiveIntegerRule, minValueRule(minValue), maxValueRule(maxValue)]"></InputPrimary>
		</div>
		<div class="form__checkboxes">
			<GlobalCheckbox v-model="formValues.consentToProcessData" required :rules="[requiredRule]">
				<p>
					{{ $t('modules.long_term_page_module.long_term_page_contact.form.consent_to_process_data') }}
					<NuxtLink :to="localePath('privacy-policy')" rel="nofollow" target="_blank">{{
						$t('modules.long_term_page_module.long_term_page_contact.form.privacy_policy')
					}}</NuxtLink
					><Asterix />
				</p>
			</GlobalCheckbox>
			<GlobalCheckbox v-model="formValues.marketingConsent">
				<p>{{ $t('modules.long_term_page_module.long_term_page_contact.form.marketing_consent') }}</p>
			</GlobalCheckbox>
		</div>
		<Transition name="fade" mode="out-in">
			<div v-if="!isFormValid" class="form__fill-all-fields">{{ $t('common.fill_all_fields_star') }}(*)</div>
		</Transition>
		<ButtonPrimary class="form__btn" :disabled="!isFormValid || loader" :loading="loader" @click="submit">{{
			$t('modules.long_term_page_module.car_inquiry_modal.form.send_inquiry')
		}}</ButtonPrimary>
	</v-form>
</template>

<style scoped lang="scss" src="./Form.scss"></style>
